import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    isSameMonth,
    isSameDay,
    isToday,
    addDays,
    startOfDay,
    endOfDay,
    differenceInMinutes,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
    LuChevronLeft,
    LuChevronRight,
    LuPlus,
    LuRefreshCw,
    LuLoaderCircle,
    LuUnlink,
    LuLink,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
// AgendaPage.css é o global disfarçado mais grave do projeto: define o sistema
// de modais inteiro (.modal-overlay/-content/-header/-body/-footer), além de
// .form-group, .btn-primary/-secondary e .empty-state — 37 arquivos dependem
// dele, quase nenhum ligado a agenda. Continua importado e intocado.
// Ver docs/designsystem/estado-do-redesign.md.
import './AgendaPage.css';
import './AgendaLayout.css';
import AppointmentPanel from '../components/AppointmentPanel';
import { AgendaService, type AgendaEvent } from '../services/AgendaService';
import { ContactService, type Contact as ApiContact } from '../services/ContactService';
import { GoogleCalendarService } from '../services/GoogleCalendarService';
import { GOOGLE_CALENDAR_AUTH_MESSAGE } from './GoogleCalendarCallbackPage';
import aviso from '../lib/aviso';

interface Appointment {
    id: string;
    title: string;
    date: Date;
    time: string;
    /** HH:mm do fim. Desde 12/09 vem do cadastro, não de "início + 1h". */
    endTime: string;
    contact: string;
    description: string;
    contatoId?: number;
    googleEventId?: string | null;
    sincronizadoGoogle?: boolean;
}

const GOOGLE_REDIRECT_URI = `${window.location.origin}/integracoes/google/callback`;

/** Faixa de horas que a linha do dia sempre mostra; estica se houver evento fora. */
const HORA_MIN = 8;
const HORA_MAX = 20;

/** Quantos eventos cabem numa célula do mês antes do "+N no dia". */
const EVENTOS_POR_CELULA = 3;

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const WEEKDAYS_LONG = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];

type Vista = 'dia' | 'mes';

const AgendaPage: React.FC = () => {
    const [vista, setVista] = useState<Vista>('dia');
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [novaHora, setNovaHora] = useState<string | undefined>(undefined);
    const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [contacts, setContacts] = useState<ApiContact[]>([]);
    const [googleConectado, setGoogleConectado] = useState<boolean | null>(null);
    const [googleMenuAberto, setGoogleMenuAberto] = useState(false);
    const [agora, setAgora] = useState(new Date());
    const popupRef = useRef<Window | null>(null);
    const googleRef = useRef<HTMLDivElement | null>(null);

    /* ─── Dados ──────────────────────────────────────────────────── */

    const checkGoogleConnection = useCallback(async () => {
        const today = new Date();
        try {
            const result = await GoogleCalendarService.getAll(startOfDay(today).toISOString(), endOfDay(today).toISOString());
            setGoogleConectado(result.sucesso);
        } catch {
            setGoogleConectado(false);
        }
    }, []);

    const fetchEvents = useCallback(async () => {
        setIsLoading(true);
        try {
            const [events, contactsData] = await Promise.all([
                AgendaService.getAll(),
                ContactService.getAll(),
            ]);

            const contactMap: Record<number, string> = {};
            if (contactsData.sucesso) {
                contactsData.dados.forEach(c => { contactMap[c.contatoId] = c.nome; });
                setContacts(contactsData.dados);
            }

            const mapped: Appointment[] = events.map((event: AgendaEvent) => {
                const startDate = new Date(event.dataInicio);
                // dataFim é obrigatório na API, mas evento antigo pode chegar
                // sem ele — aí a régua continua sendo a hora cheia de antes.
                const endDate = event.dataFim ? new Date(event.dataFim) : new Date(startDate.getTime() + 3600000);
                return {
                    id: event.agendaId.toString(),
                    title: event.descricao,
                    date: startDate,
                    time: format(startDate, 'HH:mm'),
                    endTime: format(endDate, 'HH:mm'),
                    contact: event.contatoId ? (contactMap[event.contatoId] || `Contato #${event.contatoId}`) : 'Geral',
                    contatoId: event.contatoId,
                    description: event.detalhes || '',
                    googleEventId: event.googleEventId,
                    sincronizadoGoogle: event.sincronizadoGoogle,
                };
            });
            setAppointments(mapped);
        } catch (error) {
            console.error('Error fetching events:', error);
            aviso.erro('Não foi possível carregar os eventos da agenda.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchEvents(); checkGoogleConnection(); }, [fetchEvents, checkGoogleConnection]);

    // A linha do "agora" precisa andar sozinha.
    useEffect(() => {
        const t = window.setInterval(() => setAgora(new Date()), 60_000);
        return () => window.clearInterval(t);
    }, []);

    useEffect(() => {
        const handleMessage = (e: MessageEvent) => {
            if (e.data === GOOGLE_CALENDAR_AUTH_MESSAGE) {
                popupRef.current?.close();
                setIsConnecting(false);
                setGoogleConectado(true);
                aviso.ok('Conta Google conectada.');
                fetchEvents();
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [fetchEvents]);

    useEffect(() => {
        if (!googleMenuAberto) return;
        const fora = (e: MouseEvent) => {
            if (googleRef.current && !googleRef.current.contains(e.target as Node)) setGoogleMenuAberto(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [googleMenuAberto]);

    /* ─── Google ─────────────────────────────────────────────────── */

    const handleConnect = async () => {
        setGoogleMenuAberto(false);
        setIsConnecting(true);
        try {
            const url = await GoogleCalendarService.getAuthUrl(GOOGLE_REDIRECT_URI);
            if (!url) {
                aviso.erro('Não foi possível gerar o link de conexão com o Google.');
                setIsConnecting(false);
                return;
            }
            popupRef.current = window.open(url, 'google-calendar-auth', 'width=520,height=640');
            if (!popupRef.current) {
                aviso.atencao('O navegador bloqueou a janela de autenticação. Permita pop-ups para continuar.');
                setIsConnecting(false);
            }
        } catch {
            aviso.erro('Não foi possível iniciar a conexão com o Google.');
            setIsConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        setGoogleMenuAberto(false);
        if (!window.confirm('Deseja desconectar sua conta Google? Os eventos já sincronizados permanecerão na Agenda interna.')) return;
        setIsLoading(true);
        try {
            const result = await GoogleCalendarService.disconnect();
            if (result.sucesso) {
                aviso.ok('Conta Google desconectada.');
                setGoogleConectado(false);
            } else {
                aviso.erro(result.mensagem || 'Não foi possível desconectar a conta Google.');
            }
        } catch {
            aviso.erro('Não foi possível desconectar a conta Google.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSincronizar = async () => {
        setGoogleMenuAberto(false);
        if (!googleConectado) {
            aviso.atencao('Conecte sua conta Google para sincronizar.');
            return;
        }
        setIsSyncing(true);
        try {
            const result = await AgendaService.sincronizar(
                startOfMonth(currentMonth).toISOString(),
                endOfMonth(currentMonth).toISOString(),
            );
            if (result.sucesso) {
                const r = result.dados;
                aviso.ok(
                    `Sincronização concluída: ${r.eventosCriadosNoGoogle} criados no Google, ${r.eventosCriadosNaAgenda} criados na Agenda, ${r.eventosAtualizados} atualizados, ${r.eventosRemovidos} removidos.`,
                );
                fetchEvents();
            } else {
                aviso.erro(result.mensagem || 'Não foi possível sincronizar a agenda.');
            }
        } catch {
            aviso.erro('Não foi possível sincronizar a agenda.');
        } finally {
            setIsSyncing(false);
        }
    };

    /* ─── Agendamentos ───────────────────────────────────────────── */

    const handleAddAppointment = async (newApp: any) => {
        setIsLoading(true);
        try {
            const [year, month, day] = newApp.date.split('-').map(Number);
            const [hours, minutes] = newApp.time.split(':').map(Number);
            const [endHours, endMinutes] = newApp.endTime.split(':').map(Number);
            const startDate = new Date(year, month - 1, day, hours, minutes);
            const startDateTime = startDate.toISOString();
            // A duração passou a sair do formulário (decisão 3 de 12/09); era
            // "início + 1h" cravado, o que fazia todo compromisso durar uma hora.
            const endDateTime = new Date(year, month - 1, day, endHours, endMinutes).toISOString();

            const result = newApp.id
                ? await AgendaService.update({
                    agendaId: parseInt(newApp.id),
                    descricao: newApp.title,
                    dataInicio: startDateTime,
                    dataFim: endDateTime,
                    detalhes: newApp.description,
                    contatoId: newApp.contatoId ? parseInt(newApp.contatoId) : undefined,
                })
                : await AgendaService.create({
                    descricao: newApp.title,
                    dataInicio: startDateTime,
                    dataFim: endDateTime,
                    detalhes: newApp.description,
                    contatoId: newApp.contatoId ? parseInt(newApp.contatoId) : undefined,
                    sincronizarGoogle: newApp.sincronizarGoogle,
                });

            if (result.sucesso) {
                aviso.ok(newApp.id ? 'Agendamento atualizado.' : 'Agendamento criado.');
                fetchEvents();
                setIsModalOpen(false);
                setEditingAppointment(null);
            } else {
                aviso.erro(result.mensagem || 'Não foi possível salvar o agendamento.');
            }
        } catch {
            aviso.erro('Não foi possível salvar o agendamento.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveAppointment = async (id: string) => {
        if (!window.confirm('Deseja realmente remover este agendamento?')) return;
        setIsLoading(true);
        try {
            const result = await AgendaService.remove(parseInt(id));
            if (result.sucesso) {
                aviso.ok('Evento removido.');
                setIsModalOpen(false);
                setEditingAppointment(null);
                fetchEvents();
            } else {
                aviso.erro(result.mensagem || 'Não foi possível remover o agendamento.');
            }
        } catch {
            aviso.erro('Não foi possível remover o agendamento.');
        } finally {
            setIsLoading(false);
        }
    };

    /** `hora` vem da faixa clicada na linha do dia; sem ela o painel abre 09:00. */
    const abrirNovo = (data?: Date, hora?: string) => {
        if (data) setSelectedDate(data);
        setNovaHora(hora);
        setEditingAppointment(null);
        setIsModalOpen(true);
    };

    const abrirEdicao = (app: Appointment) => {
        setEditingAppointment(app);
        setIsModalOpen(true);
    };

    const irParaODia = (dia: Date) => {
        setSelectedDate(dia);
        if (!isSameMonth(dia, currentMonth)) setCurrentMonth(dia);
        setVista('dia');
    };

    /* ─── Derivados ──────────────────────────────────────────────── */

    const porDia = useMemo(() => {
        const mapa = new Map<string, Appointment[]>();
        appointments.forEach(app => {
            const chave = format(app.date, 'yyyy-MM-dd');
            const lista = mapa.get(chave) ?? [];
            lista.push(app);
            mapa.set(chave, lista);
        });
        mapa.forEach(lista => lista.sort((a, b) => a.date.getTime() - b.date.getTime()));
        return mapa;
    }, [appointments]);

    const doDia = useCallback((d: Date) => porDia.get(format(d, 'yyyy-MM-dd')) ?? [], [porDia]);

    const eventosDoDia = useMemo(() => doDia(selectedDate), [doDia, selectedDate]);

    // A faixa de horas cobre o expediente, mas estica se houver evento fora dele.
    const horas = useMemo(() => {
        let min = HORA_MIN;
        let max = HORA_MAX;
        eventosDoDia.forEach(app => {
            const h = app.date.getHours();
            if (h < min) min = h;
            if (h + 1 > max) max = h + 1;
        });
        return Array.from({ length: Math.max(1, max - min) }, (_, i) => min + i);
    }, [eventosDoDia]);

    const proximoEvento = useMemo(() => {
        if (!isToday(selectedDate)) return null;
        return eventosDoDia.find(app => app.date.getTime() > agora.getTime()) ?? null;
    }, [eventosDoDia, selectedDate, agora]);

    const proximos = useMemo(() => {
        const de = startOfDay(addDays(selectedDate, 1)).getTime();
        const ate = endOfDay(addDays(selectedDate, 7)).getTime();
        return appointments
            .filter(app => app.date.getTime() >= de && app.date.getTime() <= ate)
            .sort((a, b) => a.date.getTime() - b.date.getTime());
    }, [appointments, selectedDate]);

    const diasDoMes = useMemo(() => {
        const inicio = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
        const fim = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });
        const dias: Date[] = [];
        for (let d = inicio; d <= fim; d = addDays(d, 1)) dias.push(d);
        return dias;
    }, [currentMonth]);

    /* ─── Pedaços de UI ──────────────────────────────────────────── */

    const acoes = (
        <div className="ag-actions">
            <div className="ag-seg" role="group" aria-label="Visão da agenda">
                <button type="button" className={vista === 'dia' ? 'is-active' : ''}
                    aria-pressed={vista === 'dia'} onClick={() => setVista('dia')}>Dia</button>
                <button type="button" className={vista === 'mes' ? 'is-active' : ''}
                    aria-pressed={vista === 'mes'} onClick={() => setVista('mes')}>Mês</button>
            </div>

            <button type="button" className="ag-btn" onClick={() => {
                const hoje = new Date();
                setSelectedDate(hoje);
                setCurrentMonth(hoje);
            }}>Hoje</button>

            {/* Desconectar é destrutivo: mora no menu, não competindo com o primário. */}
            <div className="ag-google" ref={googleRef}>
                <button type="button" className="ag-btn"
                    aria-expanded={googleMenuAberto}
                    onClick={() => setGoogleMenuAberto(v => !v)}
                    disabled={googleConectado === null}>
                    {isSyncing || isConnecting
                        ? <LuLoaderCircle size={13} className="spin" />
                        : <span className={`ag-dot ${googleConectado ? 'is-google' : ''}`}
                            style={googleConectado ? undefined : { backgroundColor: 'var(--ag-ghost)' }} />}
                    {googleConectado === null ? 'Google…' : googleConectado ? 'Google conectado' : 'Google desconectado'}
                </button>

                {googleMenuAberto && (
                    <div className="ag-google-menu" role="menu">
                        {googleConectado ? (
                            <>
                                <button type="button" onClick={handleSincronizar} disabled={isSyncing}>
                                    <LuRefreshCw size={13} /> Sincronizar este mês
                                </button>
                                <hr />
                                <button type="button" className="is-danger" onClick={handleDisconnect}>
                                    <LuUnlink size={13} /> Desconectar conta
                                </button>
                            </>
                        ) : (
                            <button type="button" onClick={handleConnect} disabled={isConnecting}>
                                <LuLink size={13} /> Conectar conta Google
                            </button>
                        )}
                    </div>
                )}
            </div>

            <button type="button" className="ag-btn is-primary" onClick={() => abrirNovo()}>
                <LuPlus size={14} /> Novo agendamento
            </button>
        </div>
    );

    const pontos = (dia: Date) => {
        const lista = doDia(dia);
        if (lista.length === 0) return null;
        const temInterno = lista.some(a => !a.sincronizadoGoogle);
        const temGoogle = lista.some(a => a.sincronizadoGoogle);
        return (
            <span className="ag-mini-dots">
                {temInterno && <i className="ag-dot" />}
                {temGoogle && <i className="ag-dot is-google" />}
            </span>
        );
    };

    const trilho = (
        <div className="ag-rail">
            <div className="ag-card ag-pad">
                <div className="ag-mini-head">
                    <button type="button" className="ag-arrow" aria-label="Mês anterior"
                        onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}><LuChevronLeft size={12} /></button>
                    <span className="ag-mini-month">{format(currentMonth, 'MMMM yyyy', { locale: ptBR })}</span>
                    <button type="button" className="ag-arrow" aria-label="Próximo mês"
                        onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}><LuChevronRight size={12} /></button>
                </div>

                <div className="ag-mini-grid">
                    {WEEKDAYS.map((d, i) => <span className="ag-mini-wd" key={i}>{d}</span>)}
                    {diasDoMes.map(dia => {
                        const fora = !isSameMonth(dia, currentMonth);
                        const sel = isSameDay(dia, selectedDate);
                        return (
                            <button
                                type="button"
                                key={dia.toISOString()}
                                className={`ag-mini-day ${fora ? 'is-outside' : ''} ${sel ? 'is-selected' : ''} ${isToday(dia) ? 'is-today' : ''}`}
                                onClick={() => irParaODia(dia)}
                                aria-label={format(dia, "d 'de' MMMM", { locale: ptBR })}
                            >
                                {format(dia, 'd')}
                                {pontos(dia)}
                            </button>
                        );
                    })}
                </div>

                <div className="ag-legend">
                    <span><i className="ag-dot" />interno</span>
                    <span><i className="ag-dot is-google" />Google</span>
                </div>
            </div>

            <div className="ag-card ag-pad">
                <h3 className="ag-title">Próximos</h3>
                <p className="ag-sub" style={{ marginBottom: 'var(--space-2)' }}>
                    7 dias · {proximos.length} {proximos.length === 1 ? 'compromisso' : 'compromissos'}
                </p>

                {proximos.length === 0 ? (
                    <p className="ag-empty">Nada nos próximos 7 dias.</p>
                ) : proximos.slice(0, 6).map(app => (
                    <button type="button" className="ag-next-row" key={app.id} onClick={() => abrirEdicao(app)}>
                        <span className="ag-next-date">
                            <b>{format(app.date, 'dd')}</b>{format(app.date, 'EEEEEE', { locale: ptBR })}
                        </span>
                        <span>
                            <span className="ag-next-title">{app.title}</span>
                            <span className="ag-next-meta">{app.time} · {app.contact}</span>
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );

    const linhaDoDia = (
        <div className="ag-card ag-pad ag-day-card">
            <div className="ag-day-head">
                <div>
                    <h3 className="ag-day-title">{format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR })}</h3>
                    <p className="ag-sub">
                        {eventosDoDia.length} {eventosDoDia.length === 1 ? 'compromisso' : 'compromissos'}
                        {proximoEvento && ` · o próximo em ${differenceInMinutes(proximoEvento.date, agora)} min`}
                    </p>
                </div>
                {horas.length > 0 && (
                    <span className="ag-day-span">
                        {String(horas[0]).padStart(2, '0')}:00 – {String(horas[horas.length - 1] + 1).padStart(2, '0')}:00
                    </span>
                )}
            </div>

            <div className="ag-timeline">
                {horas.map(h => {
                    const daHora = eventosDoDia.filter(app => app.date.getHours() === h);
                    const mostraAgora = isToday(selectedDate) && agora.getHours() === h;
                    return (
                        <div className="ag-hour" key={h}>
                            <div className="ag-hour-label">{String(h).padStart(2, '0')}:00</div>
                            <div className="ag-hour-slot">
                                {daHora.length === 0
                                    ? <button
                                        type="button"
                                        className="ag-slot-novo"
                                        aria-label={`Agendar às ${String(h).padStart(2, '0')}:00`}
                                        onClick={() => abrirNovo(selectedDate, `${String(h).padStart(2, '0')}:00`)}
                                    />
                                    : daHora.map(app => (
                                        <button
                                            type="button"
                                            key={app.id}
                                            className={`ag-block ${app.sincronizadoGoogle ? 'is-google' : ''}`}
                                            onClick={() => abrirEdicao(app)}
                                        >
                                            <span className="ag-block-title">{app.title}</span>
                                            <span className="ag-block-meta">
                                                {app.time} – {app.endTime}
                                                {' · '}{app.contact}
                                                {app.sincronizadoGoogle && ' · Google'}
                                            </span>
                                        </button>
                                    ))}
                            </div>
                            {mostraAgora && (
                                <div className="ag-now" style={{ top: `${(agora.getMinutes() / 60) * 100}%` }}>
                                    <span className="ag-now-label">{format(agora, 'HH:mm')}</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const visaoMes = (
        <div className="ag-card ag-month">
            <div className="ag-month-nav">
                <button type="button" className="ag-arrow" aria-label="Mês anterior"
                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}><LuChevronLeft size={13} /></button>
                <span className="ag-mini-month">{format(currentMonth, 'MMMM yyyy', { locale: ptBR })}</span>
                <button type="button" className="ag-arrow" aria-label="Próximo mês"
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}><LuChevronRight size={13} /></button>
                <span className="ag-spacer" />
                <span className="ag-legend" style={{ margin: 0, padding: 0, border: 'none' }}>
                    <span><i className="ag-dot" />interno</span>
                    <span><i className="ag-dot is-google" />Google</span>
                </span>
            </div>

            <div className="ag-dow">
                {WEEKDAYS_LONG.map(d => <span key={d}>{d}</span>)}
            </div>

            <div className="ag-week">
                {diasDoMes.map(dia => {
                    const lista = doDia(dia);
                    const fora = !isSameMonth(dia, currentMonth);
                    const excedente = lista.length - EVENTOS_POR_CELULA;
                    return (
                        <div
                            key={dia.toISOString()}
                            className={`ag-cell ${fora ? 'is-outside' : ''} ${isSameDay(dia, selectedDate) ? 'is-selected' : ''} ${isToday(dia) ? 'is-today' : ''}`}
                            /* Era um duplo clique que abria a criação com a data — porta
                               invisível que ninguém achava. Agora o clique leva ao dia, onde
                               a criação por horário existe (decisão 5 de 12/09). */
                            onClick={() => irParaODia(dia)}
                        >
                            <div className="ag-cell-num"><b>{format(dia, 'd')}</b></div>

                            {lista.slice(0, EVENTOS_POR_CELULA).map(app => (
                                <button
                                    type="button"
                                    key={app.id}
                                    className={`ag-pill ${app.sincronizadoGoogle ? 'is-google' : ''}`}
                                    title={`${app.time} · ${app.title} · ${app.contact}`}
                                    /* A célula inteira leva ao dia; a pílula tem destino
                                       próprio, então não deixa o clique subir. */
                                    onClick={e => { e.stopPropagation(); abrirEdicao(app); }}
                                >
                                    <i className={`ag-dot ${app.sincronizadoGoogle ? 'is-google' : ''}`} />
                                    <span className="ag-pill-hour">{app.time}</span>
                                    <span className="ag-pill-title">{app.title}</span>
                                </button>
                            ))}

                            {excedente > 0 && (
                                <button type="button" className="ag-more" onClick={() => irParaODia(dia)}>
                                    +{excedente} no dia
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="page-shell ag-screen">
            <PageHeader
                title="Agenda"
                subtitle={vista === 'dia'
                    ? 'O seu dia, hora a hora — e o que vem em seguida'
                    : 'O mês inteiro, com o horário de cada compromisso'}
                actions={acoes}
            />

            <div className="page-body ag-fill">
                {isLoading && appointments.length === 0 ? (
                    <div className="ag" aria-busy="true">
                        <div className="ag-skeleton" style={{ height: 268 }} />
                        <div className="ag-skeleton" style={{ height: 520 }} />
                    </div>
                ) : (
                    <div className="ag">
                        {vista === 'dia' ? (
                            <>
                                {trilho}
                                {linhaDoDia}
                            </>
                        ) : visaoMes}
                    </div>
                )}
            </div>

            <AppointmentPanel
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingAppointment(null); }}
                onSave={handleAddAppointment}
                onDelete={handleRemoveAppointment}
                initialDate={selectedDate}
                initialTime={novaHora}
                contacts={contacts}
                editingAppointment={editingAppointment}
                googleConectado={!!googleConectado}
                salvando={isLoading}
            />
        </div>
    );
};

export default AgendaPage;
